import { BaseService } from './BaseService';
import type {
  AssistantObject,
  AssistantListResponse,
  ThreadObject,
  MessageObject,
  RunObject,
  RunStepObject,
  CreateAssistantRequest,
  CreateThreadRequest,
  CreateMessageRequest,
  CreateRunRequest,
} from '../types/assistantsApi';

/**
 * Service for the slug-scoped Assistants API.
 * All endpoints sit under /v1/agents/{slug}/...
 */
export class AssistantsApiService extends BaseService {
  private base(slug: string) {
    return `/v1/agents/${slug}`;
  }

  // ─── Assistants ───────────────────────────────────────────────────────
  async listAssistants(slug: string, limit = 20): Promise<AssistantListResponse<AssistantObject>> {
    return this.get(`${this.base(slug)}/assistants?limit=${limit}`);
  }

  async getAssistant(slug: string, assistantId: string): Promise<AssistantObject> {
    return this.get(`${this.base(slug)}/assistants/${assistantId}`);
  }

  async createAssistant(slug: string, data: CreateAssistantRequest): Promise<AssistantObject> {
    return this.post(`${this.base(slug)}/assistants`, data);
  }

  async modifyAssistant(slug: string, assistantId: string, data: Partial<CreateAssistantRequest>): Promise<AssistantObject> {
    return this.post(`${this.base(slug)}/assistants/${assistantId}`, data);
  }

  async deleteAssistant(slug: string, assistantId: string): Promise<void> {
    return this.delete(`${this.base(slug)}/assistants/${assistantId}`);
  }

  // ─── Threads ──────────────────────────────────────────────────────────
  async listThreads(slug: string, limit = 20): Promise<AssistantListResponse<ThreadObject>> {
    return this.get(`${this.base(slug)}/threads?limit=${limit}`);
  }

  async createThread(slug: string, data?: CreateThreadRequest): Promise<ThreadObject> {
    return this.post(`${this.base(slug)}/threads`, data ?? {});
  }

  async getThread(slug: string, threadId: string): Promise<ThreadObject> {
    return this.get(`${this.base(slug)}/threads/${threadId}`);
  }

  async deleteThread(slug: string, threadId: string): Promise<void> {
    return this.delete(`${this.base(slug)}/threads/${threadId}`);
  }

  // ─── Messages ─────────────────────────────────────────────────────────
  async listMessages(slug: string, threadId: string, limit = 50, order = 'asc'): Promise<AssistantListResponse<MessageObject>> {
    return this.get(`${this.base(slug)}/threads/${threadId}/messages?limit=${limit}&order=${order}`);
  }

  async createMessage(slug: string, threadId: string, data: CreateMessageRequest): Promise<MessageObject> {
    return this.post(`${this.base(slug)}/threads/${threadId}/messages`, data);
  }

  // ─── Runs ─────────────────────────────────────────────────────────────
  async listRuns(slug: string, threadId: string, limit = 20): Promise<AssistantListResponse<RunObject>> {
    return this.get(`${this.base(slug)}/threads/${threadId}/runs?limit=${limit}`);
  }

  async getRun(slug: string, threadId: string, runId: string): Promise<RunObject> {
    return this.get(`${this.base(slug)}/threads/${threadId}/runs/${runId}`);
  }

  async createRun(slug: string, threadId: string, data: CreateRunRequest): Promise<RunObject> {
    return this.post(`${this.base(slug)}/threads/${threadId}/runs`, data);
  }

  async cancelRun(slug: string, threadId: string, runId: string): Promise<RunObject> {
    return this.post(`${this.base(slug)}/threads/${threadId}/runs/${runId}/cancel`);
  }

  // ─── Run Steps ────────────────────────────────────────────────────────
  async listRunSteps(slug: string, threadId: string, runId: string, limit = 20): Promise<AssistantListResponse<RunStepObject>> {
    return this.get(`${this.base(slug)}/threads/${threadId}/runs/${runId}/steps?limit=${limit}`);
  }
}

export const assistantsApiService = new AssistantsApiService();
